import styled from '@emotion/styled'
import {
  AutoStack,
  DayPickerStrings,
  FlexSpinner,
  FormikGraphQLDatePicker,
  onFormatDate,
} from 'components'
import { StatusText } from 'components/StatusText'
import { FormikTagPicker } from 'components/TagPicker/FormikTagPicker'
import {
  FormikDropdown,
  FormikSpinButton,
  FormikTextField,
} from 'formik-office-ui-fabric-react'
import { FieldStack, FieldStackItem, FormStack } from 'forms/FormStack'
import { Box } from 'grid'
import { isInProvingRole } from 'helpers'
import { useAuthContext } from 'hooks/useAuthContext'
import { useCurrentFormik } from 'hooks/useCurrentFormikContext'
import { useCurrentProving } from 'hooks/useCurrentProving'
import { OrderIdDistribution, ProvingUserRole, User, UserRole } from 'model'
import {
  DayOfWeek,
  IDropdownOption,
  ITag,
  Label,
  Link,
  Stack,
  Text,
} from 'office-ui-fabric-react'
import { Position } from 'office-ui-fabric-react/lib/utilities/positioning'
import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { useMaterialsQuery } from 'views/ProvingDetailsView/hooks'

const StyledSectionTitle = styled.div`
  padding: 15px 0 3px 0;
  border-bottom: 1px solid;
`

export const MaterialDetailsForm: React.FC = () => {
  const { t } = useTranslation()
  const [proving] = useCurrentProving()
  const { values } = useCurrentFormik()
  const { data, loading, error } = useMaterialsQuery()
  const pageGeneral = window.location.href.split('?')[0] + '?page=general'
  const pageProvingGround =
    window.location.origin + '/_admin/data/proving-grounds'
  const auth = useAuthContext()
  const isAdmin = auth.hasUserRole(UserRole.SystemAdministrator)
  const isResourceManager =
    auth.hasUserRole(UserRole.ResourceManager) ||
    isInProvingRole(proving, ProvingUserRole.ResourceManager)
  const materials: ITag[] =
    data && !loading
      ? data.materials.map((material) => ({
          key: material._id,
          name: material.label,
        }))
      : []
  const sortedMaterials = materials.sort((a, b) =>
    a.name < b.name ? -1 : a.name > b.name ? 1 : 0
  )

  const orderIds = proving.orderIdDistribution!.map(
    (x: OrderIdDistribution) => x.orderId
  )

  const transportDropdown: IDropdownOption[] = [
    { key: 'outboundAndReturnTransport', text: 'Hin- und Rücktransport' },
    { key: 'outboundTransport', text: 'nur Hintransport' },
    { key: 'returnTransport', text: 'nur Rücktransport' },
    { key: 'noTransport', text: 'Kein Transport' },
  ]

  // function compare(a: any, b: any) {
  //   const bandA = a.name.toUpperCase()
  //   const bandB = b.name.toUpperCase()

  //   let comparison = 0
  //   if (bandA > bandB) {
  //     comparison = 1
  //   } else if (bandA < bandB) {
  //     comparison = -1
  //   }
  //   return comparison
  // }

  return (
    <FlexSpinner loading={loading} error={error}>
      <FormStack>
        <StyledSectionTitle>
          <Text variant="xLarge" as="h2">
            {t('field.provingTitle.label')}
          </Text>
        </StyledSectionTitle>
        <>
          <Stack>
            <Stack horizontal={true} tokens={{ childrenGap: 12 }}>
              <Label>{t('field.provingTitle.label')}:</Label>
              <Box py={1}>
                <Link href={pageGeneral} target="_blank" title="Zur Erprobung">
                  {`${proving.title}, ${proving.id}`}
                </Link>
              </Box>
            </Stack>
            <Stack horizontal={true} tokens={{ childrenGap: 12 }}>
              <Label>{t('field.provingGroundTitle.label')}:</Label>
              <Box py={1}>
                {isAdmin || isResourceManager ? (
                  <Link
                    href={pageProvingGround}
                    target="_blank"
                    title="Zum Standort"
                    onClick={() => {
                      localStorage.setItem(
                        'currentProvingGround',
                        JSON.stringify(proving.provingGround)
                      )
                    }}
                  >
                    {proving.provingGround ? proving.provingGround.title : ''}
                  </Link>
                ) : (
                  <Text>
                    {proving.provingGround ? proving.provingGround.title : ''}
                  </Text>
                )}
              </Box>
            </Stack>
            <Stack horizontal={true} tokens={{ childrenGap: 12 }}>
              <Label>{t('field.provingStatus.label')}:</Label>
              <Box py={1}>
                <StatusText status={proving!.status || 0} />
              </Box>
            </Stack>
          </Stack>

          <Stack.Item className="composite-field">
            <Label>{t('field.responsible.label')}</Label>
            <Box py={1}>
              <AutoStack horizontal={true} tokens={{ childrenGap: 12 }}>
                {proving.responsible && proving.responsible.length > 0
                  ? proving.responsible.map((user: User, i: any) => {
                      return <Text key={i}>{user.name}</Text>
                    })
                  : ''}
              </AutoStack>
            </Box>
          </Stack.Item>
        </>
        <StyledSectionTitle>
          <Text variant="xLarge" as="h2">
            {t('field.materialBooking.label')}
          </Text>
        </StyledSectionTitle>
        <>
          <FieldStack>
            <FieldStackItem
              name="material"
              render={(fieldProps) => (
                <FormikTagPicker
                  tags={sortedMaterials}
                  label={t('field.materialType.label')}
                  {...fieldProps}
                  required={true}
                  disabled={true}
                />
              )}
            />
            <FieldStackItem
              name="isDangerousGood"
              label={t('field.isDangerousGood.label')}
              render={(fieldProps) => (
                <>
                  <Label required={true}>
                    {t('field.isDangerousGood.label')}
                  </Label>
                  <Text>
                    {fieldProps.form.values.isDangerousGood ? 'Ja' : 'Nein'}
                  </Text>
                </>
              )}
            />

            {values.isDangerousGood && (
              <>
                <FieldStackItem
                  name="dangerousGood.unNumber"
                  label={t('field.dangerousGood.unNumber.label')}
                  component={FormikTextField}
                  required={true}
                  disabled={true}
                  render={(fieldProps) => (
                    <>
                      <Label required={true}>
                        {' '}
                        {t('field.dangerousGood.unNumber.label')}{' '}
                      </Label>

                      <Text>
                        {`${fieldProps.field.value}${
                          fieldProps.form.values.dangerousGood.unNumber
                            ? ` - ${fieldProps.form.values.dangerousGood.description}`
                            : ''
                        }
                        `}
                      </Text>
                    </>
                  )}
                />
              </>
            )}
            <FieldStackItem
              name="dateFrom"
              label={t('field.dateFrom.label')}
              isRequired={true}
              disabled={true}
              component={FormikGraphQLDatePicker}
              // minDate={validateDateFrom()}
              // maxDate={validateDateTo()}
              firstDayOfWeek={DayOfWeek.Monday}
              strings={DayPickerStrings}
              showWeekNumbers={true}
              formatDate={onFormatDate}
              initialPickerDate={new Date(proving.dateFrom)}
            />
            <FieldStackItem
              name="dateTo"
              label={t('field.dateTo.label')}
              isRequired={true}
              disabled={true}
              component={FormikGraphQLDatePicker}
              // minDate={validateDateFrom()}
              // maxDate={validateDateTo()}
              firstDayOfWeek={DayOfWeek.Monday}
              strings={DayPickerStrings}
              showWeekNumbers={true}
              formatDate={onFormatDate}
              today={new Date(proving.dateTo)}
              initialPickerDate={new Date(proving.dateFrom)}
            />
            <FieldStackItem
              name={'amount'}
              render={(fieldProps) => (
                <FormikSpinButton
                  label={t('field.amount.label')}
                  labelPosition={Position.top}
                  max={10}
                  min={1}
                  {...fieldProps}
                  disabled={true}
                />
              )}
            />
            <FieldStackItem
              name={'orderId'}
              render={(fieldProps) => (
                <FormikDropdown
                  label={t('field.orderId.label')}
                  {...fieldProps}
                  options={[
                    ...orderIds.map(
                      (orderId) =>
                        ({
                          key: orderId,
                          text: orderId,
                        } as IDropdownOption)
                    ),
                  ]}
                  required={true}
                  disabled={true}
                />
              )}
            />
            <FieldStackItem
              name={'transport.transportType'}
              render={(fieldProps) => (
                <FormikDropdown
                  label={t('field.transport.label')}
                  {...fieldProps}
                  options={transportDropdown}
                  required={true}
                  disabled={true}
                />
              )}
            />
            <FieldStackItem
              name={'annotation'}
              render={(fieldProps) => (
                <FormikTextField
                  label={t('field.annotation.label')}
                  {...fieldProps}
                  required={false}
                  disabled={true}
                />
              )}
            />
            <FieldStackItem
              name={'group'}
              render={(fieldProps) => (
                <FormikTextField
                  label={t('field.group.label')}
                  {...fieldProps}
                  required={false}
                  disabled={true}
                />
              )}
            />
          </FieldStack>
        </>
      </FormStack>
    </FlexSpinner>
  )
}
